import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, ButtonBase, Checkbox, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { topicsDrawerActionsStyles, topicsDrawerBackdropStyles, topicsDrawerCheckboxStyles, topicsDrawerClearButtonStyles, topicsDrawerCloseButtonStyles, topicsDrawerGroupHeaderStyles, topicsDrawerHeaderStyles, topicsDrawerListStyles, topicsDrawerPaperStyles, topicsDrawerRowStyles, topicsDrawerSelectedListStyles, topicsDrawerTitleStyles, topicsPillStyles } from './BlogListPage.styles';
import { getHrefPath } from '../TAGLink/TAGLink.helpers';
import Drawer from '@shared-components/components/Drawer';
import { tagToSlug, tagToTitle } from '@utils/pages/tag-url';
interface IBlogTagFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  blogTagPath: string;
  allTags: string[];
  activeTagSegments: string[];
  migrationPaths?: string[];
}
interface IGroupedTags {
  letter: string;
  tags: string[];
}
const groupTagsByLetter = (tags: string[]): IGroupedTags[] => {
  const sortedTags = [...tags].sort((left, right) => left.localeCompare(right));
  const groups = new Map<string, string[]>();
  sortedTags.forEach(tag => {
    const letter = tag.charAt(0).toUpperCase();
    const group = groups.get(letter) ?? [];
    group.push(tag);
    groups.set(letter, group);
  });
  return Array.from(groups.entries()).map(([letter, groupTags]) => ({
    letter,
    tags: groupTags
  }));
};
export default function BlogTagFilterDrawer({
  open,
  onClose,
  blogTagPath,
  allTags,
  activeTagSegments,
  migrationPaths
}: Readonly<IBlogTagFilterDrawerProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchValue, setSearchValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Active segments come from the URL as slugs ("oral-health") while allTags holds the raw
  // Contentful values ("Oral Health"), so they're matched on their slug form.
  const activeTags = useMemo(() => allTags.filter(tag => activeTagSegments.includes(tagToSlug(tag))), [allTags, activeTagSegments]);
  useEffect(() => {
    if (open) {
      setSelectedTags(activeTags);
      setSearchValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTags]);
  const filteredTags = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return allTags;
    }
    return allTags.filter(tag => tag.toLowerCase().includes(query));
  }, [allTags, searchValue]);
  const groupedTags = useMemo(() => groupTagsByLetter(filteredTags), [filteredTags]);
  const handleToggleTag = (tag: string) => {
    setSelectedTags(previousSelectedTags => previousSelectedTags.includes(tag) ? previousSelectedTags.filter(selectedTag => selectedTag !== tag) : [...previousSelectedTags, tag]);
  };
  const handleClearFilters = () => {
    setSelectedTags([]);
  };
  const handleShowResults = () => {
    const nextPath = selectedTags.length ? `${blogTagPath}${selectedTags.map(tag => tagToSlug(tag)).join('/')}` : blogTagPath;
    globalThis.window.location.href = getHrefPath(nextPath, migrationPaths);
  };
  const clearButtonLabel = isMobile ? 'Clear' : 'Clear filters';
  return <Drawer anchor={isMobile ? 'bottom' : 'right'} open={open} onClose={onClose} data-testid="blog_tag_filter_drawer" ModalProps={{
    BackdropProps: {
      sx: topicsDrawerBackdropStyles
    }
  }} PaperProps={{
    sx: topicsDrawerPaperStyles
  }} data-sentry-element="Drawer" data-sentry-component="BlogTagFilterDrawer" data-sentry-source-file="BlogTagFilterDrawer.tsx">
			<Box sx={topicsDrawerHeaderStyles} data-sentry-element="Box" data-sentry-source-file="BlogTagFilterDrawer.tsx">
				<Box sx={topicsDrawerTitleStyles} data-sentry-element="Box" data-sentry-source-file="BlogTagFilterDrawer.tsx">
					<Typography data-testid="blog_tag_filter_drawer_title" variant="header2" color="text.primary" component="h2" data-sentry-element="Typography" data-sentry-source-file="BlogTagFilterDrawer.tsx">
						Topics
					</Typography>
					<ButtonBase onClick={onClose} sx={topicsDrawerCloseButtonStyles} data-testid="blog_tag_filter_drawer_close" aria-label="Close drawer" data-sentry-element="ButtonBase" data-sentry-source-file="BlogTagFilterDrawer.tsx">
						<CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="BlogTagFilterDrawer.tsx" />
					</ButtonBase>
				</Box>
				<Box sx={{
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }} data-sentry-element="Box" data-sentry-source-file="BlogTagFilterDrawer.tsx">
					<TextField fullWidth placeholder="Search Topics" value={searchValue} onChange={event => setSearchValue(event.target.value)} data-testid="blog_tag_filter_drawer_search" data-sentry-element="TextField" data-sentry-source-file="BlogTagFilterDrawer.tsx" />
				</Box>
				{!!selectedTags.length && <Box sx={topicsDrawerSelectedListStyles} data-testid="blog_tag_filter_drawer_selected_list">
						{selectedTags.map(tag => <Button key={tag} variant="secondaryDefault" size="XS" onClick={() => handleToggleTag(tag)} endIcon={<CloseIcon />} sx={topicsPillStyles} data-testid={`blog_tag_filter_drawer_selected_${tagToSlug(tag)}`}>
								{tag}
							</Button>)}
					</Box>}
			</Box>
			<Box sx={topicsDrawerListStyles} data-testid="blog_tag_filter_drawer_list" data-sentry-element="Box" data-sentry-source-file="BlogTagFilterDrawer.tsx">
				{groupedTags.map(group => <Box key={group.letter}>
						<Typography data-testid={`blog_tag_filter_drawer_group_${group.letter}`} variant="bodyMediumSemiBold" color="text.tertiary" component="h3" sx={topicsDrawerGroupHeaderStyles}>
							{group.letter}
						</Typography>
						{group.tags.map(tag => <Stack key={tag} direction="row" alignItems="center" sx={topicsDrawerRowStyles} onClick={() => handleToggleTag(tag)} data-testid={`blog_tag_filter_drawer_row_${tagToSlug(tag)}`}>
								<Checkbox sx={topicsDrawerCheckboxStyles} checked={selectedTags.includes(tag)} tabIndex={-1} />
								<Typography variant="bodyMediumBook" color="text.secondary">
									{tagToTitle(tag)}
								</Typography>
							</Stack>)}
					</Box>)}
			</Box>
			<Box sx={topicsDrawerActionsStyles} data-sentry-element="Box" data-sentry-source-file="BlogTagFilterDrawer.tsx">
				<Button variant="tertiaryDefault" size="M" onClick={handleClearFilters} sx={topicsDrawerClearButtonStyles} data-testid="blog_tag_filter_drawer_clear" data-sentry-element="Button" data-sentry-source-file="BlogTagFilterDrawer.tsx">
					{clearButtonLabel}
				</Button>
				<Button variant="primaryDefault" size="M" onClick={handleShowResults} data-testid="blog_tag_filter_drawer_show_results" data-sentry-element="Button" data-sentry-source-file="BlogTagFilterDrawer.tsx">
					Show results
				</Button>
			</Box>
		</Drawer>;
}